import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCustomerDetail } from "@/hooks/useCustomerDetail";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { X, Package, MapPin, Phone } from "lucide-react";
import { formatDetailDate } from "@/lib/dateUtils";
import type { Order } from "@/types/order";

function OrderHistoryItem({ order, onClick }: { order: Order; onClick: (id: string) => void }) {
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5 px-1 border-b border-border/30 cursor-pointer hover:bg-muted/30 transition-colors rounded"
      onClick={() => onClick(order.id)}
      role="button"
      tabIndex={0}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">
          {order.order_number ? `#${order.order_number}` : order.parsed_items?.slice(0, 40) || "Order"}
        </p>
        <p className="text-xs text-muted-foreground">
          {order.delivered_at ? formatDetailDate(order.delivered_at) : ""}
        </p>
      </div>
      {order.total_price != null && (
        <span className="text-sm font-semibold tabular-nums shrink-0">
          {Number(order.total_price).toFixed(0)}₾
        </span>
      )}
    </div>
  );
}

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useLanguage();
  const { customer, customerLoading, orders, ordersLoading } = useCustomerDetail(customerId);

  const handleClose = () => navigate("/customers");

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const revenue = parseFloat(customer?.total_revenue || "0");
  const lastDate = customer?.last_delivered_at
    ? formatDetailDate(customer.last_delivered_at)
    : "—";

  const addresses = customer?.recent_addresses?.filter(Boolean) || [];
  const lastAddress = addresses[0] || null;
  const otherAddresses = addresses.slice(1);

  return (
    <Sheet open={true} modal={true}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          handleClose();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleClose();
        }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border/50">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold truncate">
              {customerLoading ? "..." : customer?.name || t("customers.notFound")}
            </h2>
            {customer?.phone && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span>{customer.phone}</span>
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} className="shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {customerLoading ? (
            <div className="py-12 flex justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !customer ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              {t("customers.notFound")}
            </div>
          ) : (
            <div className="space-y-5 py-4">
              {/* Stats Strip — signal-strength hierarchy */}
              <div className="mx-4 bg-muted/30 rounded-lg px-4 py-3 flex items-baseline gap-4">
                {/* Revenue: dominant */}
                <div className="flex-1">
                  <p className="text-2xl font-bold tabular-nums">
                    {revenue > 0 ? `${revenue.toFixed(0)}₾` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("customers.stats.revenue")}</p>
                </div>
                <div className="w-px h-8 bg-border shrink-0" />
                {/* Orders: medium */}
                <div className="flex-1">
                  <p className="text-lg font-semibold tabular-nums">
                    {customer.delivered_count > 0 ? String(customer.delivered_count) : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("customers.stats.orders")}</p>
                </div>
                <div className="w-px h-8 bg-border shrink-0" />
                {/* Last order: calm */}
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground tabular-nums">{lastDate}</p>
                  <p className="text-xs text-muted-foreground/70">{t("customers.stats.lastOrder")}</p>
                </div>
              </div>

              {/* Addresses — contained */}
              {lastAddress && (
                <div className="mx-4 bg-muted/20 rounded-lg px-4 py-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {t("customers.lastAddress")}
                  </div>
                  <p className="text-sm pl-5">{lastAddress}</p>

                  {otherAddresses.length > 0 && (
                    <div className="pl-5 space-y-1 mt-1">
                      <p className="text-xs text-muted-foreground/70">{t("customers.recentAddresses")}</p>
                      {otherAddresses.map((addr, i) => (
                        <p key={i} className="text-xs text-muted-foreground">{addr}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Separator className="mx-4" />

              {/* Order History */}
              <div className="px-4">
                <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground mb-2">
                  <Package className="h-3.5 w-3.5 shrink-0" />
                  {t("customers.orderHistory")}
                </div>

                {ordersLoading ? (
                  <div className="py-4 text-center">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">{t("customers.noDeliveredOrders")}</p>
                ) : (
                  <div>
                    {orders.map((order) => (
                      <OrderHistoryItem
                        key={order.id}
                        order={order}
                        onClick={handleOrderClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
